import type { MaintenancePlanResponse, PlanNodeOut } from '@/client';
import {
	asPlanKind,
	ATTENTION_STATUSES,
	DISPLAY_ONLY_KINDS,
	PlanKind,
	PlanStatuses,
} from '@/components/maintenance/model/planVocabulary';

/**
 * Reads over the plan endpoint's own types. There is deliberately no `PlanNode` of our own.
 *
 * The endpoint is already a read model for this UI — the backend merges the internal streams, groups
 * a task appearing across systems into one card, and applies the display overlays before it answers.
 * Adapting a view model that was designed for the view is redundant, and it costs: a second type to
 * maintain, a reverse mapping for writes, and three edits per new field instead of one. Where our
 * model genuinely differed from the API's, the answer turned out to be deleting ours
 * (`maintenanceRelationships.ts`), not mapping onto it.
 *
 * These are the reads with a decision in them — a predicate, a count, a filter. A field the client
 * already types is read straight off the node instead: `node.status` needs no accessor to restate
 * what the union says. `kind` is the one exception, because `schemas.py` still sends it as `str`.
 *
 * So: components take `PlanNodeOut` and call these. Writes send `node.action_target` straight back,
 * with nothing in between to keep honest.
 */

export const planKind = (node: PlanNodeOut): PlanKind | null => asPlanKind(node.kind);

/**
 * What the *endpoint* calls a group. Not the same question as "does it hold anything".
 *
 * Meaningful when talking to the API — it is what the history rollup keys on — and misleading as a
 * proxy for structure. Use {@link subtaskCount} or {@link withChildren} to ask about children.
 */
export const isGroup = (node: PlanNodeOut): boolean => node.node_type === 'group';

/**
 * How many entries a card folds in, including display-only procedure steps and coverage clauses.
 *
 * A card can carry children without being typed as a group: gating a child list on `node_type` hid
 * five behind a parent the endpoint had typed as a leaf. `child_count` is the endpoint's canonical
 * aggregate. It answers a different question from {@link taskChildren}, which excludes supporting
 * information when calculating child-task progress.
 */
export const subtaskCount = (node: PlanNodeOut): number => node.child_count || (node.children?.length ?? 0);

export const hasSubtasks = (node: PlanNodeOut): boolean => subtaskCount(node) > 0;

const TASK_CHILD_KINDS: ReadonlySet<PlanKind> = new Set([
	PlanKind.ExplicitTask,
	PlanKind.Checklist,
	PlanKind.Compliance,
	PlanKind.Custom,
]);

/** What a card contains, without calling display-only procedure steps separate tasks. */
export function childItemNoun(node: PlanNodeOut): 'step' | 'task' | 'item' {
	if (!withChildren(node)) return 'item';

	const kinds = node.children.map(planKind);
	if (kinds.every((kind) => kind === PlanKind.ProcedureStep)) return 'step';
	if (kinds.every((kind) => kind !== null && TASK_CHILD_KINDS.has(kind))) return 'task';
	return 'item';
}

/** A node whose children arrived with it, so they can be read rather than counted. */
export type PlanNodeWithChildren = PlanNodeOut & { children: PlanNodeOut[] };

/**
 * Narrows to a node whose children are actually in hand.
 *
 * `children` remains optional in the generated type, so a caller that maps over an arbitrary card
 * has to prove they are present. Group cards are formed before pagination and bring their children
 * with them; this guard is defensive narrowing, not permission to treat a partial group as normal.
 */
export const withChildren = (node: PlanNodeOut): node is PlanNodeWithChildren => (node.children?.length ?? 0) > 0;

/** Procedure instructions carried by a task; they have no independent state or actions. */
export const procedureChildren = (node: PlanNodeOut): PlanNodeOut[] =>
	(node.children ?? []).filter((child) => planKind(child) === PlanKind.ProcedureStep);

/** Non-display-only children rendered as tasks rather than read-only supporting information. */
export const taskChildren = (node: PlanNodeOut): PlanNodeOut[] =>
	(node.children ?? []).filter((child) => !isDisplayOnly(child));

/**
 * Names a group fan-out without counting read-only procedure or coverage rows as separate tasks.
 * One task child (or none) needs no "all 1" claim; the verb already describes the decision.
 */
export const groupActionLabel = (verb: string, node: PlanNodeOut): string => {
	const count = taskChildren(node).length;
	return count > 1 ? `${verb} all ${count}` : verb;
};

/** The inert container each nested task belongs to, keyed by the child the schedule can open. */
export const planParents = (nodes: readonly PlanNodeOut[]): ReadonlyMap<string, { id: string; title: string }> => {
	const parents = new Map<string, { id: string; title: string }>();
	for (const node of nodes) {
		if (!isGroup(node)) continue;
		for (const child of taskChildren(node)) parents.set(child.node_id, { id: node.node_id, title: node.title });
	}
	return parents;
};

/**
 * A node that marks cover rather than asking for work — an expiry date or a covered system.
 *
 * Kept as one predicate because the timeline lifts these out of the day rows onto their own markers
 * while the row draws them with a different chip, and the two must agree on what counts.
 */
export const isWarrantyNode = (node: PlanNodeOut): boolean => {
	const kind = planKind(node);
	return kind === PlanKind.WarrantyExpiry || kind === PlanKind.WarrantySystem;
};

export interface PlanGroupProgress {
	completed: number;
	total: number;
	/** The exact child tasks represented by both counts. */
	children: PlanNodeOut[];
}

/**
 * How far through a group's non-display-only child tasks the user is, together with those tasks.
 *
 * Returns `null` for a non-group, a group whose children were not supplied, or a group containing
 * only display-only information. The endpoint sends an aggregate `child_count` but no count by kind
 * or completed tally. Groups are formed before pagination and their children travel with them, so
 * both progress counts come from the child tasks in hand and describe the same set.
 */
export function groupProgress(node: PlanNodeOut): PlanGroupProgress | null {
	if (!isGroup(node) || !withChildren(node)) return null;

	const children = taskChildren(node);
	if (children.length === 0) return null;

	const completed = children.filter((child) => child.status === PlanStatuses.Completed).length;
	return { completed, total: children.length, children };
}

/** Renders, but nobody can act on it — a coverage clause or a procedure step. */
export function isDisplayOnly(node: PlanNodeOut): boolean {
	const kind = planKind(node);
	return kind !== null && DISPLAY_ONLY_KINDS.has(kind);
}

export const isActionable = (node: PlanNodeOut): boolean =>
	!isDisplayOnly(node) && (node.allowed_actions?.length ?? 0) > 0;

export const needsAttention = (node: PlanNodeOut): boolean => !!node.status && ATTENTION_STATUSES.has(node.status);

/** Depth-first over the tree the server grouped. Most reads want it flat. */
export function* walk(nodes: readonly PlanNodeOut[]): Generator<PlanNodeOut> {
	for (const node of nodes) {
		yield node;
		yield* walk(node.children ?? []);
	}
}

/** `plan_nodes` is nullable on the wire; every caller wants a list. */
/**
 * A card's own words, with the household's correction applied where they made one.
 *
 * An edit on a pipeline-derived card is a *truthful overlay*: the endpoint records the override in
 * `review.edited_fields` and deliberately leaves the document's wording in `title` beside it, so
 * both survive. The contract says to render the override in place of the card's own field, and
 * `review.document_values` keeps what it displaced so a revert can name it.
 *
 * Only a custom entry is edited in the entity itself, which is why reading `title` straight looked
 * right on those and stale everywhere else.
 */
const withHouseholdCorrections = (node: PlanNodeOut): PlanNodeOut => {
	const edits = node.review?.edited_fields;
	const children = node.children?.map(withHouseholdCorrections);
	if (!edits?.title && !edits?.description) return children ? { ...node, children } : node;

	return {
		...node,
		title: edits.title ?? node.title,
		description: edits.description ?? node.description,
		...(children ? { children } : {}),
	};
};

/**
 * The endpoint's cadence sentence in this app's words.
 *
 * The server says "Annually"; every control here says "Yearly", and a card that said one beside a
 * picker offering the other read as two frequencies. Rewritten here rather than at each chip so the
 * row, the drawer, the review card and the sheet cannot drift apart on it. Anything else passes
 * through as written.
 */
const withHouseWording = (node: PlanNodeOut): PlanNodeOut => {
	const children = node.children?.map(withHouseWording);
	const cadenceText = node.schedule?.cadence?.text;
	const reworded = cadenceText && /^annually$/i.test(cadenceText) ? 'Yearly' : cadenceText;
	if (reworded === cadenceText && !children) return node;

	return {
		...node,
		...(reworded !== cadenceText
			? { schedule: { ...node.schedule, cadence: { ...node.schedule?.cadence, text: reworded } } }
			: {}),
		...(children ? { children } : {}),
	};
};

/**
 * Every card in the plan, corrected once at the read boundary.
 *
 * The correction is applied here rather than at each place a title is drawn: the list, the timeline,
 * the drawer, search and alphabetical ordering all read these nodes, and asking each of them to
 * remember the overlay is how half of them end up showing the document's wording instead. One
 * funnel, so there is one answer to what a card is called, and to what its cadence is called.
 */
export const planNodeForDisplay = (node: PlanNodeOut): PlanNodeOut => withHouseholdCorrections(withHouseWording(node));

export const planNodes = (plan: MaintenancePlanResponse): PlanNodeOut[] =>
	(plan.plan_nodes ?? []).map(planNodeForDisplay);
